import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Check, X, WifiOff } from 'lucide-react';
import { trainerKalenderAPI } from '@/lib/api';
import { useWachBleiben } from '@/hooks/useStation';

/* Anwesenheits-Tafel fuer das iPad an der Saeule (06.10.2026).

   Link ohne Login: /tafel/<token> — derselbe Token wie ein Trainer-Link (routes/trainer_kalender),
   deshalb laeuft nichts ab. Empfehlung: einen eigenen Trainer-Link „Säulen-iPad" anlegen, dann
   laesst sich die Tafel einzeln sperren oder erneuern.

   Ausgelegt auf das iPad 10.2" (810 × 1080 hoch, 1080 × 810 quer): grosse Kacheln, dunkler
   Hintergrund, Status nur ueber Farbe + Symbol + Wort. Die Kacheln bleiben alphabetisch stehen,
   damit beim Einchecken niemand „wegspringt".

   Verhalten
   - laedt alle 20 s neu; bei Verbindungsfehler bleibt der letzte Stand stehen (Hinweis oben)
   - waehlt von selbst den laufenden bzw. naechsten Kurs; nach manueller Wahl 10 Min. Ruhe,
     dann wieder automatisch
   - Antippen einer Kachel: angemeldet → eingecheckt → fehlt → angemeldet (wie /trainer)
   - ?lesen=1 haengt man an, wenn die Tafel nur anzeigen soll (kein Antippen)
   - haelt den Bildschirm wach (Wake Lock, iPadOS ab 16.4) */

const NEU_LADEN_MS = 20000;
const AUTO_ZURUECK_MS = 10 * 60 * 1000;
const NAECHSTER = { bestaetigt: 'anwesend', anwesend: 'gefehlt', gefehlt: 'bestaetigt' };

const FARBE = {
  orange: '#ef7c23',
  grund: '#0d0d0f',
  flaeche: '#1a1a1e',
  rand: '#2c2c33',
  text: '#f5f5f4',
  leise: '#9b9ba3',
  gruen: '#22c55e',
  rot: '#ef4444',
};

const minuten = (hhmm) => { if (!hhmm) return null; const [h, m] = hhmm.split(':').map(Number); return h * 60 + m; };
const jetztMin = () => { const d = new Date(); return d.getHours() * 60 + d.getMinutes(); };

function automatischerKurs(kurse) {
  if (!kurse?.length) return null;
  const n = jetztMin();
  const aktiv = kurse.filter((k) => !k.ausfall);
  // laeuft gerade (10 Min. Vorlauf: Trainer pruefen vor Kursbeginn)
  const laufend = aktiv.find((k) => minuten(k.start) - 10 <= n && n < minuten(k.end));
  if (laufend) return laufend.course_id;
  const naechster = aktiv.find((k) => minuten(k.start) > n);
  if (naechster) return naechster.course_id;
  return (aktiv[aktiv.length - 1] || kurse[kurse.length - 1]).course_id;
}

function kursZeile(k) {
  const n = jetztMin();
  const s = minuten(k.start); const e = minuten(k.end);
  if (k.ausfall) return 'fällt aus';
  if (n >= e) return 'vorbei';
  if (n >= s) return `läuft · noch ${e - n} Min.`;
  const d = s - n;
  return d < 60 ? `beginnt in ${d} Min.` : `beginnt ${k.start} Uhr`;
}

/* Vollbild auf dem iPad (06.10.2026): Safari › Teilen › „Zum Home-Bildschirm" legt die Tafel als
   eigene App ab. Mit diesen Angaben startet sie ohne Adressleiste. Nur auf dieser Seite gesetzt —
   das restliche CRM bleibt als Lesezeichen normal in Safari. Kein Manifest: iOS uebernaehme sonst
   dessen start_url statt des Tafel-Links mit Token. */
function useVollbildApp() {
  useEffect(() => {
    const gesetzt = [];
    const meta = (name, content) => {
      const m = document.createElement('meta'); m.name = name; m.content = content;
      document.head.appendChild(m); gesetzt.push(m);
    };
    meta('apple-mobile-web-app-capable', 'yes');
    meta('mobile-web-app-capable', 'yes');
    meta('apple-mobile-web-app-status-bar-style', 'black-translucent');
    meta('apple-mobile-web-app-title', 'P1 Tafel');
    meta('theme-color', FARBE.grund);
    const icon = document.createElement('link'); icon.rel = 'apple-touch-icon'; icon.href = '/media/p1-logo.png';
    document.head.appendChild(icon); gesetzt.push(icon);
    const vp = document.querySelector('meta[name="viewport"]');
    const alt = vp?.content;
    if (vp) vp.content = 'width=device-width, initial-scale=1, viewport-fit=cover';
    const titel = document.title; document.title = 'P1 Tafel';
    return () => { gesetzt.forEach((e) => e.remove()); if (vp && alt) vp.content = alt; document.title = titel; };
  }, []);
}

function Uhr() {
  const [zeit, setZeit] = useState(new Date());
  useEffect(() => { const t = setInterval(() => setZeit(new Date()), 15000); return () => clearInterval(t); }, []);
  return (
    <div style={{ textAlign: 'right', lineHeight: 1.05 }}>
      <div style={{ fontSize: 76, fontWeight: 800, letterSpacing: -2, fontVariantNumeric: 'tabular-nums' }}>
        {zeit.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}
      </div>
      <div style={{ fontSize: 20, fontWeight: 600, color: FARBE.leise }}>
        {zeit.toLocaleDateString('de-DE', { weekday: 'long', day: '2-digit', month: '2-digit' })}
      </div>
    </div>
  );
}

/* Kachelgroesse fuer Fernlesbarkeit (06.10.2026), gestaffelt nach Teilnehmerzahl: so gross wie
   moeglich, aber ein Kurs soll auf dem iPad 10.2 ohne Scrollen auf den Bildschirm passen. */
const GROESSE = {
  gross: { spalte: 320, name: 38, status: 21, kreis: 66, haken: 40, hoehe: 128, abstand: 16, pad: '16px 20px' },
  mittel: { spalte: 245, name: 30, status: 18, kreis: 52, haken: 32, hoehe: 104, abstand: 12, pad: '12px 14px' },
  klein: { spalte: 200, name: 24, status: 15, kreis: 42, haken: 26, hoehe: 84, abstand: 10, pad: '10px 12px' },
};
const groesseFuer = (n) => (n <= 6 ? GROESSE.gross : n <= 12 ? GROESSE.mittel : GROESSE.klein);

function Kachel({ t, onTap, busy, lesen, g }) {
  const da = t.status === 'anwesend';
  const fehlt = t.status === 'gefehlt';
  const hintergrund = da ? 'rgba(34,197,94,0.16)' : fehlt ? 'rgba(239,68,68,0.10)' : FARBE.flaeche;
  const rand = da ? FARBE.gruen : fehlt ? 'rgba(239,68,68,0.55)' : FARBE.rand;
  const statusText = da ? 'eingecheckt' : fehlt ? 'fehlt' : 'noch nicht da';
  const statusFarbe = da ? FARBE.gruen : fehlt ? FARBE.rot : FARBE.orange;
  return (
    <button
      type="button"
      onClick={lesen ? undefined : () => onTap(t)}
      disabled={busy}
      data-testid={`tafel-tn-${t.buchung_id}`}
      aria-label={`${t.name}: ${statusText}`}
      style={{
        background: hintergrund, border: `3px solid ${rand}`, borderRadius: 20, padding: g.pad,
        display: 'flex', alignItems: 'center', gap: g.abstand, textAlign: 'left', color: FARBE.text, minHeight: g.hoehe,
        opacity: busy ? 0.5 : fehlt ? 0.75 : 1, cursor: lesen ? 'default' : 'pointer',
        WebkitTapHighlightColor: 'transparent', touchAction: 'manipulation', transition: 'background 200ms, border-color 200ms',
      }}
    >
      <span style={{
        flexShrink: 0, width: g.kreis, height: g.kreis, borderRadius: 999, display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: da ? FARBE.gruen : fehlt ? FARBE.rot : 'transparent', border: da || fehlt ? 'none' : `3px dashed ${FARBE.orange}`,
      }}>
        {da && <Check size={g.haken} strokeWidth={3.5} color="#0d0d0f" />}
        {fehlt && <X size={g.haken - 4} strokeWidth={3.5} color="#fff" />}
      </span>
      <span style={{ minWidth: 0, flex: 1 }}>
        <span style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', fontSize: g.name, fontWeight: 800, lineHeight: 1.1, overflow: 'hidden', overflowWrap: 'break-word', hyphens: 'auto', WebkitHyphens: 'auto' }} lang="de">
          {t.name || 'Ohne Namen'}
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6, fontSize: g.status, fontWeight: 700, color: statusFarbe }}>
          {statusText}
          {t.minderjaehrig && (
            <span style={{ fontSize: 14, fontWeight: 700, color: FARBE.leise, border: `1px solid ${FARBE.rand}`, borderRadius: 999, padding: '1px 8px' }}>
              Kind{t.abgeholt ? ' · abgeholt' : ''}
            </span>
          )}
        </span>
      </span>
    </button>
  );
}

export default function AnwesenheitKioskPage() {
  const { token } = useParams();
  const lesen = useMemo(() => new URLSearchParams(window.location.search).has('lesen'), []);
  const [daten, setDaten] = useState(null);
  const [fehler, setFehler] = useState(null); // 'link' | 'netz' | null
  const [stand, setStand] = useState(null);
  const [gewaehlt, setGewaehlt] = useState(null);
  const [busy, setBusy] = useState(null);
  const manuellSeit = useRef(0);
  useWachBleiben(true);
  useVollbildApp();

  const lade = useCallback(async () => {
    try {
      const d = (await trainerKalenderAPI.anwesenheit(token)).data;
      setDaten(d); setFehler(null); setStand(new Date());
    } catch (e) {
      setFehler(e?.response?.status === 404 ? 'link' : 'netz');
    }
  }, [token]);
  useEffect(() => { lade(); const t = setInterval(lade, NEU_LADEN_MS); return () => clearInterval(t); }, [lade]);
  // nach Ruhezeit oder Seitenwechsel wieder auf den automatischen Kurs
  useEffect(() => {
    const t = setInterval(() => { if (manuellSeit.current && Date.now() - manuellSeit.current > AUTO_ZURUECK_MS) { manuellSeit.current = 0; setGewaehlt(null); } }, 30000);
    const sichtbar = () => { if (document.visibilityState === 'visible') lade(); };
    document.addEventListener('visibilitychange', sichtbar);
    return () => { clearInterval(t); document.removeEventListener('visibilitychange', sichtbar); };
  }, [lade]);

  const kurse = daten?.kurse || [];
  const autoId = automatischerKurs(kurse);
  const aktivId = kurse.some((k) => k.course_id === gewaehlt) ? gewaehlt : autoId;
  const kurs = kurse.find((k) => k.course_id === aktivId);
  const teilnehmer = useMemo(
    () => [...(kurs?.teilnehmer || [])].sort((a, b) => (a.name || '').localeCompare(b.name || '', 'de')),
    [kurs],
  );
  const zahlen = useMemo(() => ({
    gebucht: teilnehmer.length,
    da: teilnehmer.filter((t) => t.status === 'anwesend').length,
    offen: teilnehmer.filter((t) => t.status === 'bestaetigt').length,
    fehlt: teilnehmer.filter((t) => t.status === 'gefehlt').length,
  }), [teilnehmer]);

  const g = groesseFuer(teilnehmer.length);

  const waehlen = (id) => { manuellSeit.current = id === autoId ? 0 : Date.now(); setGewaehlt(id === autoId ? null : id); };

  const tap = async (t) => {
    const ziel = NAECHSTER[t.status] || 'anwesend';
    setBusy(t.buchung_id);
    // sofort zeigen, dann vom Server bestaetigen lassen
    setDaten((d) => d && ({ ...d, kurse: d.kurse.map((k) => k.course_id !== aktivId ? k : ({ ...k, teilnehmer: k.teilnehmer.map((x) => x.buchung_id === t.buchung_id ? { ...x, status: ziel } : x) })) }));
    try { await trainerKalenderAPI.anwesenheitStatus(token, t.buchung_id, ziel); } catch { /* naechstes Laden zeigt den echten Stand */ }
    finally { setBusy(null); lade(); }
  };

  const seite = {
    minHeight: '100dvh', background: FARBE.grund, color: FARBE.text, fontFamily: "'Manrope', 'Inter', system-ui, sans-serif",
    padding: 'max(16px, env(safe-area-inset-top)) 20px 20px', boxSizing: 'border-box', userSelect: 'none', WebkitUserSelect: 'none',
  };

  if (fehler === 'link') {
    return (
      <div style={{ ...seite, display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>
        <div><p style={{ fontSize: 28, fontWeight: 800 }}>Link ungültig oder gesperrt</p>
          <p style={{ fontSize: 17, color: FARBE.leise, marginTop: 8 }}>Im CRM unter Kalender › Trainer-Links einen neuen Tafel-Link kopieren.</p></div>
      </div>
    );
  }

  return (
    <div style={seite} data-testid="anwesenheit-tafel-kiosk">
      {/* Kopf */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <img src="/media/p1-logo-weiss.png" alt="P1" style={{ height: 52, width: 'auto' }} onError={(e) => { e.currentTarget.style.display = 'none'; }} />
          <div style={{ fontSize: 40, fontWeight: 800, letterSpacing: -0.5 }}>Anwesenheit</div>
        </div>
        <Uhr />
      </div>

      {fehler === 'netz' && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'rgba(239,124,35,0.15)', border: `2px solid ${FARBE.orange}`, borderRadius: 12, padding: '10px 14px', marginBottom: 14, fontSize: 16, fontWeight: 600 }}>
          <WifiOff size={20} /> Keine Verbindung — Stand {stand ? stand.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }) : '–'} Uhr
        </div>
      )}

      {/* Kursleiste */}
      <div style={{ display: 'flex', gap: 10, overflowX: 'auto', paddingBottom: 6, marginBottom: 16, WebkitOverflowScrolling: 'touch' }}>
        {kurse.map((k) => {
          const an = k.course_id === aktivId;
          const vorbei = jetztMin() >= minuten(k.end) || k.ausfall;
          return (
            <button key={k.course_id} type="button" onClick={() => waehlen(k.course_id)} data-testid={`tafel-kurs-${k.course_id}`}
              style={{
                flexShrink: 0, minWidth: 170, textAlign: 'left', borderRadius: 14, padding: '10px 14px', color: FARBE.text,
                background: an ? FARBE.orange : FARBE.flaeche, border: `2px solid ${an ? FARBE.orange : FARBE.rand}`,
                opacity: vorbei && !an ? 0.45 : 1, touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent',
              }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: an ? '#1a0d02' : FARBE.leise, fontVariantNumeric: 'tabular-nums' }}>{k.start}–{k.end}</div>
              <div style={{ fontSize: 17, fontWeight: 800, color: an ? '#1a0d02' : FARBE.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 220 }}>{k.name}</div>
              <div style={{ fontSize: 13, fontWeight: 600, color: an ? '#1a0d02' : FARBE.leise }}>{k.ausfall ? 'fällt aus' : `${k.anwesend}/${k.gebucht} da`}</div>
            </button>
          );
        })}
        {daten && kurse.length === 0 && <p style={{ fontSize: 20, color: FARBE.leise }}>Heute keine Kurse.</p>}
        {!daten && !fehler && <p style={{ fontSize: 20, color: FARBE.leise }}>Lädt …</p>}
      </div>

      {kurs && (
        <>
          {/* Kurskopf mit Zahlen */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, marginBottom: 10 }}>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ fontSize: 40, fontWeight: 800, lineHeight: 1.05, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{kurs.name}</div>
              <div style={{ fontSize: 21, fontWeight: 600, color: FARBE.leise, marginTop: 6 }}>
                {kurs.start}–{kurs.end} Uhr{kurs.trainer ? ` · ${kurs.trainer}` : ''} · <span style={{ color: FARBE.orange, fontWeight: 700 }}>{kursZeile(kurs)}</span>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              {[[zahlen.da, 'eingecheckt', FARBE.gruen], [zahlen.offen, 'noch nicht da', FARBE.orange], ...(zahlen.fehlt ? [[zahlen.fehlt, 'fehlt', FARBE.rot]] : [])].map(([z, l, f]) => (
                <div key={l} style={{ background: FARBE.flaeche, border: `2px solid ${FARBE.rand}`, borderRadius: 16, padding: '8px 12px', textAlign: 'center', minWidth: 112, flexShrink: 0 }}>
                  <div style={{ fontSize: 60, fontWeight: 800, color: f, lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>{z}</div>
                  <div style={{ fontSize: 15, color: FARBE.leise, fontWeight: 700, marginTop: 2, whiteSpace: 'nowrap' }}>{l}</div>
                </div>
              ))}
            </div>
          </div>
          <div style={{ height: 8, borderRadius: 99, background: FARBE.flaeche, overflow: 'hidden', marginBottom: 18 }}>
            <div style={{ height: '100%', width: `${zahlen.gebucht ? (zahlen.da / zahlen.gebucht) * 100 : 0}%`, background: FARBE.gruen, transition: 'width 400ms' }} />
          </div>

          {/* Kacheln */}
          <div style={{ display: 'grid', gridTemplateColumns: `repeat(auto-fill, minmax(${g.spalte}px, 1fr))`, gap: g.abstand }} data-testid="tafel-kacheln">
            {teilnehmer.map((t) => <Kachel key={t.buchung_id} t={t} onTap={tap} busy={busy === t.buchung_id} lesen={lesen} g={g} />)}
          </div>
          {teilnehmer.length === 0 && (
            <p style={{ fontSize: 22, color: FARBE.leise, textAlign: 'center', marginTop: 40 }}>Für diesen Kurs ist noch niemand angemeldet.</p>
          )}
          {!lesen && teilnehmer.length > 0 && (
            <p style={{ fontSize: 14, color: FARBE.leise, textAlign: 'center', marginTop: 18 }}>Antippen: noch nicht da → eingecheckt → fehlt</p>
          )}
        </>
      )}
    </div>
  );
}
